import { Chart, ScatterDataPoint } from "chart.js";
import _, {  } from "lodash";
import { MapBrowserEvent } from "ol";
import { Coordinate } from "ol/coordinate";
import LineString from "ol/geom/LineString";
import {Options} from "ol/interaction/Pointer";
import { getLength } from "ol/sphere";
import { TrackEntity } from './TrackEntity'
import TrackLineFeature from "./TrackLineFeature";
import TrackVectorLayer from "./TrackVectorLayer";
import Point from "ol/geom/Point";
import { Feature } from "ol";
import SingleFeatureSource from "./SingleFeatureSource";
import { DistanceUnit } from "./DistanceUnits";
import findNearestDataPoint from "./BinaryChop";
import EventRememberingPointerInteraction from "./EventRememberingPointerInteraction";

class TrackLineFeatureWithCoordinate {
    feature: TrackLineFeature
    coord: Coordinate
    constructor(feature: TrackLineFeature, coord: Coordinate) {
        this.feature = feature
        this.coord = coord
    }
}

// shows the position hovered on the map, on the elevation chart.
export default class ElevationChartHoverIndicator extends EventRememberingPointerInteraction {
    elevationChart: Chart
    track: TrackEntity
    handlePointerMoveDebounced = _.debounce(this.updateElevationChart.bind(this))
    indicatorFeatureSource: SingleFeatureSource<Point>
    debugFeature = null as null|Feature<Point>
    map = this.getMap() // sometimes seems to return null inside updateElevationChart, something to do with the fact it's debounced
    distanceUnits: DistanceUnit
    constructor(elevationChart: Chart, track: TrackEntity, indicatorFeatureSource: SingleFeatureSource<Point>, distanceUnits: DistanceUnit, options?: Options) {
        super(options)
        this.elevationChart = elevationChart
        this.track = track
        this.indicatorFeatureSource = indicatorFeatureSource
        this.distanceUnits = distanceUnits
    }

    handleEvent(mapBrowserEvent: MapBrowserEvent<any>): boolean {
        if(mapBrowserEvent.type === 'pointermove') {
            this.handlePointerMoveDebounced(mapBrowserEvent) //getMap sometimes returns null if called within updateElevationChart,
            return true
        }
        return super.handleEvent(mapBrowserEvent)
    }

    static interpolate(p1: ScatterDataPoint, p2: ScatterDataPoint, proportion: number): ScatterDataPoint {
        return {
            x: (p1.x + (p2.x - p1.x) * proportion),
            y: (p1.y + (p2.y - p1.y) * proportion)
        }
    }
    
    updateElevationChart(event: MapBrowserEvent<any>) {
        if(!this.getMap()) {
            return // being disposed as the map's redrawn
        }

        const features = this.getMap().getFeaturesAtPixel(event.pixel, { hitTolerance: 15, layerFilter: l => l instanceof TrackVectorLayer && l.track === this.track})
            .filter(f => f instanceof TrackLineFeature)
            .map(f => f as TrackLineFeature)

        const featuresWithClosest = features.map(f => new TrackLineFeatureWithCoordinate(f, f.getGeometry().getClosestPoint(event.coordinate)))
        const nearest = _.first(_.sortBy(featuresWithClosest, f => getLength(new LineString([event.coordinate, f.coord]))))
        const indicatorDataset = _.last(this.elevationChart.data.datasets)
        if(!indicatorDataset) {
            throw new Error("No indicator dataset on chart")
        }
        const indicatorData = indicatorDataset.data
        let needToUpdate = false
        while(indicatorData.length > 0) {// should only be 1
            indicatorData.pop()
            needToUpdate = true
        }
        if(nearest) {
            // Map-to-ElevChart visualisation...

            this.indicatorFeatureSource.getOrAddFeature().setGeometry(new Point(nearest.coord))
            const distanceAlongTrack = this.track.getDistanceAlong(nearest.coord)
            const allElevationPoints = this.elevationChart.data.datasets[0].data as ScatterDataPoint[]

            if(distanceAlongTrack >= 0 && allElevationPoints.length > 0) {
                const nearestElevPointIndex =  findNearestDataPoint(allElevationPoints, distanceAlongTrack, p => p.x) // this will never be a NaN point
                const nearestElevPoint = allElevationPoints[nearestElevPointIndex] as ScatterDataPoint // this will never be a NaN point
               
                let elevationPointBefore = nearestElevPoint, elevationPointAfter = nearestElevPoint
                
                if (distanceAlongTrack >= nearestElevPoint.x) {
                    if (nearestElevPointIndex+1 < allElevationPoints.length) {
                        elevationPointAfter = allElevationPoints[nearestElevPointIndex+1] as ScatterDataPoint 
                        elevationPointAfter = isNaN(elevationPointAfter.x) ? elevationPointBefore : elevationPointAfter
                    }
                } else {
                    if (nearestElevPointIndex > 0) {
                        elevationPointBefore = allElevationPoints[nearestElevPointIndex-1] as ScatterDataPoint 
                        elevationPointBefore = isNaN(elevationPointBefore.x) ? elevationPointAfter : elevationPointBefore
                    }
                }
                
                let elevationPointIndication = nearestElevPoint
                if (elevationPointBefore.x != elevationPointAfter.x) {
                    const proportionBetweenPoints = (distanceAlongTrack - elevationPointBefore.x) / (elevationPointAfter.x - elevationPointBefore.x)  
                    elevationPointIndication = ElevationChartHoverIndicator.interpolate(elevationPointBefore, elevationPointAfter, proportionBetweenPoints)
                }
                
                indicatorData.push(elevationPointIndication)
                needToUpdate = true
            }
        } else {
            this.indicatorFeatureSource.clear()
            this.debugFeature = null
        }

        if(needToUpdate) {
            this.elevationChart.update("none")
        }
    }
}